import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

/**
 * Billing Success Page
 * Displayed after successful Stripe Checkout
 * Verifies the checkout session and shows success message
 */
const BillingSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifySession = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      if (!user) {
        // If not logged in, redirect to auth
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Wait a moment for webhook to process
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Check entitlements to verify subscription was created
        const response = await api.get('/billing/entitlements');
        
        if (response.data?.success) {
          const data = response.data.data;
          
          // Check if user now has an active subscription or trial
          if (data.entitled || data.billingStatus === 'TRIALING' || data.billingStatus === 'ACTIVE') {
            setSuccess(true);
            
            // Redirect to dashboard after 5 seconds
            setTimeout(() => {
              navigate('/dashboard');
            }, 5000);
          } else {
            setError('Subscription not yet active. Please wait a moment and refresh.');
          }
        } else {
          setError('Failed to verify subscription status');
        }
      } catch (err: any) {
        console.error('Error verifying session:', err);
        // Still show success if we got here (Stripe redirected us)
        setSuccess(true);
        // Redirect after a delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 5000);
      } finally {
        setLoading(false);
      }
    };

    verifySession();
  }, [sessionId, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-neutral-400">Verifying your subscription...</p>
        </div>
      </div>
    );
  }

  if (error && !success) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-neutral-900 p-8 rounded-lg border border-neutral-700 text-center">
          <div className="w-16 h-16 bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Verification Pending</h2>
          <p className="text-neutral-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
            >
              Refresh
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-neutral-600 hover:border-neutral-500 text-neutral-300 hover:text-white rounded-lg transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-neutral-900 p-8 rounded-lg border border-neutral-700 shadow-xl text-center">
        <div className="w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h1 className="text-3xl font-bold text-white mb-4">Welcome to TheTraders Pro!</h1>
        
        <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4 mb-6">
          <p className="text-green-300 font-semibold mb-2">Your 7-day free trial has started</p>
          <p className="text-neutral-300 text-sm">
            You now have full access to all Pro features. Enjoy your trial!
          </p>
        </div>

        <div className="bg-neutral-800/50 rounded-lg p-4 mb-6 text-left">
          <h3 className="text-white font-semibold mb-3">What's Next:</h3>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li className="flex items-start">
              <svg className="w-5 h-5 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Explore your dashboard and trading features</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Set up your Alpaca trading account keys</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Configure your trading preferences</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>After 7 days, you'll be charged $39.99/month (cancel anytime)</span>
            </li>
          </ul>
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors"
        >
          Go to Dashboard
        </button>

        <p className="text-xs text-neutral-500 mt-4">
          Redirecting automatically in a few seconds...
        </p>
      </div>
    </div>
  );
};

export default BillingSuccess;

